import React, { useEffect, useState } from 'react';
import SmallButton from '../../../components/SmallButton';
import Label from '../../../components/Label';
import { axiosKK } from '../../../services/networkRequest';
import {
  PartnerUserAccess,
  PartnerUserAccessListResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import { useApi } from '../../../services/useApi';
import Switch from 'react-switch';
import CreateLogisticsUserModal from '../../../components/admin/CreateLogisticsUserModal';

export default function AccessTab() {
  const [users, setUsers] = useState<PartnerUserAccess[]>([]);
  const [networkError, setNetworkError] = useState<string>();
  const [partnerFilter, setPartnerFilter] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);

  const partnerOptions = React.useMemo(() => {
    const map = new Map<number, string>();
    users.forEach((u) => {
      if (u.partnerId && u.partnerName) {
        map.set(u.partnerId, u.partnerName);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [users]);

  const { data, loading, error } = useApi<PartnerUserAccessListResponse>(
    () => {
      const params: any = {};
      if (partnerFilter) params.partnerId = Number(partnerFilter);
      return axiosKK
        .get<PartnerUserAccessListResponse>('/logistics/admin/users/access', { params })
        .then(({ data }) => data);
    },
    [partnerFilter]
  );

  useEffect(() => {
    if (data) setUsers(data.users);
  }, [data]);

  useEffect(() => {
    if (error) setNetworkError(error);
  }, [error]);

  const handleToggleAccess = (userId: number, currentValue: boolean) => {
    const newValue = !currentValue;
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, logisticsDashboardEnabled: newValue } : u
      )
    );
    axiosKK
      .put(`/logistics/admin/users/${userId}/access`, {
        logisticsDashboardEnabled: newValue,
      })
      .catch((error) => {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, logisticsDashboardEnabled: currentValue } : u
          )
        );
        errorMessage(setNetworkError)(error);
      });
  };

  const handleToggleLogisticsControl = (userId: number, currentValue: boolean) => {
    const newValue = !currentValue;
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, canControlLogistics: newValue } : u))
    );
    axiosKK
      .put(`/logistics/admin/users/${userId}/logistics-control`, {
        canControlLogistics: newValue,
      })
      .catch((error) => {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, canControlLogistics: currentValue } : u
          )
        );
        errorMessage(setNetworkError)(error);
      });
  };

  return (
    <>
      {networkError && <p className="error">{networkError}</p>}

      <section className="admin-partner-access">
        <div className="logistics-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>Partner</label>
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
              >
                <option value="">All Partners</option>
                {partnerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-item filter-item--action">
              <SmallButton onClick={() => setShowCreateUser(true)}>
                + Create Logistics User
              </SmallButton>
            </div>
          </div>
        </div>

        <div className="table">
          <div className="thead">
            <div className="tr">
              <div className="td access-col-id">ID</div>
              <div className="td access-col-name">Name</div>
              <div className="td access-col-email">Email</div>
              <div className="td access-col-role">Role</div>
              <div className="td access-col-partner">Partner</div>
              <div className="td access-col-active">Active</div>
              <div className="td access-col-toggle">Dashboard Access</div>
              <div className="td access-col-toggle">Logistics Control</div>
            </div>
          </div>
          <div className="tbody">
            {loading ? (
              <div className="tr">
                <div className="td" style={{ width: '100%', textAlign: 'center' }}>
                  Loading...
                </div>
              </div>
            ) : users.length === 0 ? (
              <div className="tr">
                <div className="td" style={{ width: '100%', textAlign: 'center' }}>
                  No users found
                </div>
              </div>
            ) : (
              users.map((user) => (
                <div className="tr" key={user.id}>
                  <Label>ID</Label>
                  <div className="td access-col-id">{user.id}</div>

                  <Label>Name</Label>
                  <div className="td access-col-name">{user.name}</div>

                  <Label>Email</Label>
                  <div className="td access-col-email">{user.email}</div>

                  <Label>Role</Label>
                  <div className="td access-col-role">{user.role}</div>

                  <Label>Partner</Label>
                  <div className="td access-col-partner">{user.partnerName}</div>

                  <Label>Active</Label>
                  <div className="td access-col-active">
                    {user.isActive ? (
                      <span className="logistics-badge logistics-badge--active">Yes</span>
                    ) : (
                      <span className="logistics-badge logistics-badge--inactive">No</span>
                    )}
                  </div>

                  <Label>Dashboard Access</Label>
                  <div className="td access-col-toggle partner-access-toggle">
                    <Switch
                      checked={user.logisticsDashboardEnabled}
                      onChange={() =>
                        handleToggleAccess(user.id, user.logisticsDashboardEnabled)
                      }
                      onColor="#6c3cd9"
                      offColor="#dbdbdb"
                      uncheckedIcon={false}
                      checkedIcon={false}
                      height={22}
                      width={44}
                      handleDiameter={18}
                    />
                  </div>

                  <Label>Logistics Control</Label>
                  <div className="td access-col-toggle partner-access-toggle">
                    <Switch
                      checked={!!user.canControlLogistics}
                      onChange={() =>
                        handleToggleLogisticsControl(user.id, !!user.canControlLogistics)
                      }
                      onColor="#008dc9"
                      offColor="#dbdbdb"
                      uncheckedIcon={false}
                      checkedIcon={false}
                      height={22}
                      width={44}
                      handleDiameter={18}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <CreateLogisticsUserModal
        show={showCreateUser}
        partnerOptions={partnerOptions}
        defaultPartnerId={partnerFilter ? Number(partnerFilter) : undefined}
        close={() => setShowCreateUser(false)}
        onSuccess={(created) => {
          setUsers((prev) => [created, ...prev]);
        }}
      />
    </>
  );
}
